// src/UserControls/Payment/AddressService.js
import { Auth } from "aws-amplify";
import configDetails from "../../Config/Config";
import Logger from "../../utils/Logger";

const BASE = configDetails.apiBaseUrlOrderservice;

const getToken = async () => {
  const user = await Auth.currentAuthenticatedUser();
  return user.signInUserSession.idToken.jwtToken;
};

export const getUserId = async () => {
  const user = await Auth.currentAuthenticatedUser();
  return user.attributes.sub; // cognitoUserId — always from Auth, never from user input
};

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

// ─── Response parser (mirrors SubscriptionService parseSmart pattern) ─────────

const parseSmart = async (resp) => {
  const clone = resp.clone();
  try {
    const ct = (resp.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) return await resp.json();
    const text = await resp.text();
    try { return JSON.parse(text); } catch { return { raw: text }; }
  } catch {
    try {
      const text = await clone.text();
      try { return JSON.parse(text); } catch { return { raw: text }; }
    } catch (e) {
      return { raw: e.message || "Failed to parse response" };
    }
  }
};

const extractError = (data) => {
  if (!data) return null;
  if (typeof data === "string") return data;
  for (const key of ["message", "error", "detail", "raw"]) {
    if (data[key] && typeof data[key] === "string") return data[key];
  }
  return null;
};

export const fetchAddresses = async (cognitoUserId) => {
  const token = await getToken();
  const url = `${BASE}/addresses?cognitoUserId=${cognitoUserId}`;
  Logger.info("📍 Fetching addresses", { cognitoUserId });

  const resp = await fetch(url, { method: "GET", headers: authHeaders(token) });

  if (!resp.ok) {
    const data = await parseSmart(resp);
    const msg = extractError(data) || `HTTP ${resp.status}`;
    Logger.error("❌ Failed to fetch addresses", { status: resp.status, msg });
    throw new Error(msg);
  }

  const data = await parseSmart(resp);
  Logger.info("✅ Addresses fetched", { count: data.addresses?.length ?? 0 });
  return data.addresses ?? [];
};

export const createAddress = async (cognitoUserId, formData) => {
  const token = await getToken();
  Logger.info("📍 Creating address", { cognitoUserId });

  const resp = await fetch(`${BASE}/addresses`, {
    method: "POST",
    headers: authHeaders(token),
    body: JSON.stringify({
      cognitoUserId,
      fullName:     formData.fullName,
      email:        formData.email,
      phone:        formData.phone || null,
      addressLine1: formData.addressLine1,
      city:         formData.city,
      state:        formData.state,
      postalCode:   formData.postalCode,
      country:      formData.country,
      isDefault:    formData.isDefault ?? false,
    }),
  });

  if (!resp.ok) {
    const data = await parseSmart(resp);
    const msg = extractError(data) || `HTTP ${resp.status}`;
    Logger.error("❌ Failed to create address", { status: resp.status, msg });
    throw new Error(msg);
  }

  const data = await parseSmart(resp);
  Logger.info("✅ Address created", { addressId: data.addressId });
  return data; // { message, addressId }
};

export const updateAddress = async (addressId, cognitoUserId, formData) => {
  const token = await getToken();
  const url = `${BASE}/addresses/${addressId}?cognitoUserId=${cognitoUserId}`;
  Logger.info("📍 Updating address", { addressId });

  const resp = await fetch(url, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({
      cognitoUserId,
      fullName:     formData.fullName,
      email:        formData.email,
      phone:        formData.phone || null,
      addressLine1: formData.addressLine1,
      city:         formData.city,
      state:        formData.state,
      postalCode:   formData.postalCode,
      country:      formData.country,
      isDefault:    formData.isDefault ?? false,
    }),
  });

  if (!resp.ok) {
    const data = await parseSmart(resp);
    const msg = extractError(data) || `HTTP ${resp.status}`;
    Logger.error("❌ Failed to update address", { status: resp.status, msg });
    throw new Error(msg);
  }

  Logger.info("✅ Address updated", { addressId });
  return true;
};

export const deleteAddress = async (addressId, cognitoUserId) => {
  const token = await getToken();
  const url = `${BASE}/addresses/${addressId}?cognitoUserId=${cognitoUserId}`;
  Logger.info("📍 Deleting address", { addressId });

  const resp = await fetch(url, { method: "DELETE", headers: authHeaders(token) });

  if (!resp.ok) {
    const data = await parseSmart(resp);
    const msg = extractError(data) || `HTTP ${resp.status}`;
    Logger.error("❌ Failed to delete address", { status: resp.status, msg });
    throw new Error(msg);
  }

  Logger.info("✅ Address deleted", { addressId });
  return true;
};

export const setDefaultAddress = async (addressId, cognitoUserId, addressData) => {
  const token = await getToken();
  const url = `${BASE}/addresses/${addressId}?cognitoUserId=${cognitoUserId}`;
  Logger.info("📍 Setting default address", { addressId });

  const resp = await fetch(url, {
    method: "PUT",
    headers: authHeaders(token),
    body: JSON.stringify({
      cognitoUserId,
      fullName:     addressData.fullName,
      email:        addressData.email,
      phone:        addressData.phone || null,
      addressLine1: addressData.addressLine1,
      city:         addressData.city,
      state:        addressData.state,
      postalCode:   addressData.postalCode,
      country:      addressData.country,
      isDefault:    true,
    }),
  });

  if (!resp.ok) {
    const data = await parseSmart(resp);
    const msg = extractError(data) || `HTTP ${resp.status}`;
    Logger.error("❌ Failed to set default address", { status: resp.status, msg });
    throw new Error(msg);
  }

  Logger.info("✅ Default address updated", { addressId });
  return true;
};

export const buildExistingAddressPayload = (address) => ({
  addressId: address.addressId
});

/**
 * New address form → billing address payload with new field naming
 */
export const buildNewAddressPayload = (formData) => ({
  billingName:         formData.fullName,
  billingEmail:        formData.email,
  billingPhoneNumber:  formData.phone || null,
  billingAddress:      formData.addressLine1,
  billingCity:         formData.city,
  billingState:        formData.state,
  billingPostalCode:   formData.postalCode,
  billingCountry:      formData.country,
});

const AddressService = {
  getUserId,
  fetchAddresses,
  createAddress,
  updateAddress,
  deleteAddress,
  setDefaultAddress,
  buildExistingAddressPayload,
  buildNewAddressPayload,
};

export default AddressService;